import { X, ExternalLink, MapPin, Clock, Shield, Tag } from 'lucide-react'
import type { MonitorEventItem } from '@/lib/api'

const SEVERITY_GLOW: Record<string, string> = {
  critical: 'bg-red-500 shadow-[0_0_6px_rgba(239,68,68,0.5)]',
  high: 'bg-orange-500 shadow-[0_0_6px_rgba(249,115,22,0.5)]',
  medium: 'bg-yellow-500 shadow-[0_0_6px_rgba(234,179,8,0.5)]',
  low: 'bg-blue-500 shadow-[0_0_6px_rgba(59,130,246,0.5)]',
  info: 'bg-gray-500',
}

interface EventDetailProps {
  event: MonitorEventItem
  onClose: () => void
}

export default function EventDetail({ event, onClose }: EventDetailProps) {
  const ts = new Date(event.timestamp)
  const ago = getTimeAgo(ts)

  return (
    <div className="bg-gray-950/95 backdrop-blur border border-green-900/50 rounded overflow-hidden max-h-[70vh] overflow-y-auto shadow-[0_0_15px_rgba(34,197,94,0.08)]">
      <div className="flex items-start justify-between px-4 py-3 border-b border-green-900/40 bg-gray-900/50">
        <div className="flex-1 min-w-0 pr-2">
          <h3 className="text-sm font-semibold text-gray-100 leading-snug">{event.title}</h3>
          <div className="flex items-center gap-2 mt-1.5 text-[10px] font-mono text-gray-500">
            <span className={`w-2 h-2 rounded-full ${SEVERITY_GLOW[event.severity] || 'bg-gray-500'}`} />
            <span className="capitalize">{event.severity}</span>
            <span className="text-gray-700">·</span>
            <span className="capitalize">{event.domain}</span>
            <span className="text-gray-700">·</span>
            <span>{event.source}</span>
          </div>
        </div>
        <button onClick={onClose} className="text-gray-600 hover:text-gray-300 transition-colors p-0.5">
          <X className="w-4 h-4" />
        </button>
      </div>

      <div className="px-4 py-3 space-y-3 text-xs font-mono">
        {event.description && (
          <p className="text-gray-400 leading-relaxed font-sans text-[11px]">{event.description}</p>
        )}

        <div className="space-y-1.5">
          <InfoRow icon={<Clock className="w-3 h-3" />} label="Time" value={`${ts.toLocaleString()} (${ago})`} />
          <InfoRow icon={<Shield className="w-3 h-3" />} label="Score" value={`${event.severity_score}/100`} />
          {event.country_name && (
            <InfoRow icon={<MapPin className="w-3 h-3" />} label="Location" value={`${event.country_name}${event.region ? ` · ${event.region}` : ''}`} />
          )}
          {event.latitude && event.longitude && (
            <InfoRow icon={<MapPin className="w-3 h-3" />} label="Coords" value={`${event.latitude.toFixed(3)}, ${event.longitude.toFixed(3)}`} />
          )}
        </div>

        {event.actors.length > 0 && (
          <div>
            <span className="text-[9px] text-gray-600 uppercase tracking-[0.15em]">Actors</span>
            <div className="flex flex-wrap gap-1 mt-1">
              {event.actors.map((a) => (
                <span key={a} className="bg-purple-500/15 text-purple-400 border border-purple-500/30 px-1.5 py-0.5 rounded text-[9px]">{a}</span>
              ))}
            </div>
          </div>
        )}

        {event.tags.length > 0 && (
          <div>
            <span className="text-[9px] text-gray-600 uppercase tracking-[0.15em] flex items-center gap-1"><Tag className="w-3 h-3" /> Tags</span>
            <div className="flex flex-wrap gap-1 mt-1">
              {event.tags.map((t) => (
                <span key={t} className="bg-gray-800/70 text-gray-500 border border-gray-700/30 px-1.5 py-0.5 rounded text-[9px]">{t}</span>
              ))}
            </div>
          </div>
        )}

        {event.metadata?.iocs && Array.isArray(event.metadata.iocs) && event.metadata.iocs.length > 0 && (
          <div>
            <span className="text-[9px] text-gray-600 uppercase tracking-[0.15em]">IOCs ({event.metadata.iocs.length})</span>
            <div className="mt-1 space-y-1 max-h-32 overflow-y-auto">
              {event.metadata.iocs.slice(0, 20).map((ioc: { value?: string; type?: string }, i: number) => (
                <div key={i} className="bg-amber-500/10 border border-amber-500/20 rounded px-2 py-0.5 font-mono text-[9px] break-all">
                  <span className="text-amber-400">{ioc.value ?? '(empty)'}</span>
                  {ioc.type && <span className="text-amber-600/80 ml-1">({ioc.type})</span>}
                </div>
              ))}
              {event.metadata.iocs.length > 20 && (
                <span className="text-[9px] text-gray-500">+{event.metadata.iocs.length - 20} more</span>
              )}
            </div>
          </div>
        )}

        {event.source_url && (
          <a
            href={event.source_url}
            target="_blank"
            rel="noopener noreferrer"
            className="inline-flex items-center gap-1 text-green-400 hover:text-green-300 transition-colors text-[10px] drop-shadow-[0_0_4px_rgba(74,222,128,0.3)]"
          >
            <ExternalLink className="w-3 h-3" /> View source
          </a>
        )}
      </div>
    </div>
  )
}

function InfoRow({ icon, label, value }: { icon: React.ReactNode; label: string; value: string }) {
  return (
    <div className="flex items-start gap-1.5">
      <span className="text-gray-600 mt-0.5">{icon}</span>
      <div className="text-[10px]">
        <span className="text-gray-600">{label}: </span>
        <span className="text-gray-400">{value}</span>
      </div>
    </div>
  )
}

function getTimeAgo(date: Date): string {
  const seconds = Math.floor((Date.now() - date.getTime()) / 1000)
  if (seconds < 60) return 'just now'
  const minutes = Math.floor(seconds / 60)
  if (minutes < 60) return `${minutes}m ago`
  const hours = Math.floor(minutes / 60)
  if (hours < 24) return `${hours}h ago`
  const days = Math.floor(hours / 24)
  return `${days}d ago`
}
